import React, { useState, useMemo } from 'react';
import { OrderOutlet, CartItem, MenuItem, AddOnOption, SelectedCustomOption } from '../../../types';
import { useContent } from '../../../context/ContentContext';
import { formatRupiah } from '../../../utils/formatters';
import { resolveMediaUrl } from '../../../utils/api';
import { calculateItemUnitPrice } from '../../../data/addOnsData';
import { ProductAddOnsModal } from './ProductAddOnsModal';
import {
  isMenuItemAvailableForOutlet,
  subscribeToOutletStockRealtime,
} from '../../../utils/supabaseStock';
import {
  subscribeToOutletAvailabilityRealtime,
  normalizeOutletKey,
} from '../../../utils/supabaseOutletStatus';
import {
  ShoppingBag,
  Search,
  Plus,
  Minus,
  Store,
  ChevronRight,
  Sparkles,
  Coffee,
  Check,
  AlertCircle,
  Lock,
  ArrowLeft,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface OrderMenuProps {
  outlet: OrderOutlet;
  cart: CartItem[];
  onAddToCart: (
    product: MenuItem,
    size?: AddOnOption,
    topping?: AddOnOption,
    syrup?: AddOnOption,
    quantity?: number,
    note?: string,
    customOptions?: SelectedCustomOption[]
  ) => void;
  onUpdateCartQuantity: (cartItemId: string, delta: number) => void;
  onOpenCart: () => void;
  onChangeOutlet: () => void;
  onBack?: () => void;
  preSelectedProduct?: MenuItem | null;
  onClearPreSelectedProduct?: () => void;
}

export const OrderMenu: React.FC<OrderMenuProps> = ({
  outlet,
  cart,
  onAddToCart,
  onUpdateCartQuantity,
  onOpenCart,
  onChangeOutlet,
  onBack,
  preSelectedProduct,
  onClearPreSelectedProduct,
}) => {
  const { data, refreshData } = useContent();
  const { menuCategories, menuItems } = data;

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [customizingProduct, setCustomizingProduct] = useState<MenuItem | null>(null);
  const [isOutletOpen, setIsOutletOpen] = useState<boolean>(true);

  // Subscribe to live outlet availability updates
  React.useEffect(() => {
    const unsub = subscribeToOutletAvailabilityRealtime((statusMap) => {
      const normKey = normalizeOutletKey(outlet?.id);
      setIsOutletOpen(statusMap[normKey] !== false);
    });
    return () => unsub();
  }, [outlet?.id]);

  // Subscribe to live stock broadcast updates
  React.useEffect(() => {
    const unsub = subscribeToOutletStockRealtime((payload) => {
      if (!payload.outletId || payload.outletId === outlet?.id) {
        refreshData();
      }
    });
    return () => unsub();
  }, [outlet?.id, refreshData]);

  // Auto-open modal if a preSelectedProduct was passed
  React.useEffect(() => {
    if (preSelectedProduct) {
      setCustomizingProduct(preSelectedProduct);
      if (onClearPreSelectedProduct) {
        onClearPreSelectedProduct();
      }
    }
  }, [preSelectedProduct, onClearPreSelectedProduct]);

  // Standard category grouping
  const categoriesList = useMemo(() => {
    return [...(menuCategories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [menuCategories]);

  // Filtered menu
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (menuItems || [])
      .filter((item) => {
        if (!item) return false;
        const matchesCategory = selectedCategory === 'all' || item.categoryId === selectedCategory;
        const itemName = (item.name || '').toLowerCase();
        const itemDesc = (item.description || '').toLowerCase();
        const matchesSearch = !q || itemName.includes(q) || itemDesc.includes(q);
        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [menuItems, selectedCategory, searchQuery]);

  // Cart summary
  const totalCartCount = useMemo(() => {
    return (cart || []).reduce((acc, item) => acc + (item?.quantity || 0), 0);
  }, [cart]);

  const totalCartAmount = useMemo(() => {
    return (cart || []).reduce(
      (acc, item) =>
        acc + calculateItemUnitPrice(item?.product?.price ?? 0, item?.size, item?.topping, item?.syrup, item?.customOptions) * (item?.quantity || 1),
      0
    );
  }, [cart]);

  const getProductQuantityInCart = (productId: string): number => {
    return (cart || [])
      .filter((it) => it?.product?.id === productId)
      .reduce((sum, it) => sum + (it?.quantity || 0), 0);
  };

  const handleOpenAddOns = (item: MenuItem) => {
    setCustomizingProduct(item);
  };

  const handleConfirmAddOns = (
    product: MenuItem,
    size: AddOnOption,
    topping: AddOnOption,
    syrup: AddOnOption,
    quantity: number,
    note?: string,
    customOptions?: SelectedCustomOption[]
  ) => {
    onAddToCart(product, size, topping, syrup, quantity, note, customOptions);
  };

  const UX_PILOT_MENU_FALLBACKS = [
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_8e3d013412_83f73dfba6b675e4.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_0d1f2d8604_59d4017316da779c.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_dac4073ceb_774de92e48b89dcf.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_caf1cef3c0_212dba6e73b63456.png',
    'https://storage.googleapis.com/uxpilot-auth.appspot.com/gen_34e13c3ec9_3da2a68e6fa9668e.png',
  ];

  return (
    <div className="w-full pb-36 bg-white text-[#0A192F] max-w-lg md:max-w-3xl lg:max-w-7xl mx-auto min-h-screen">
      {/* Header — Exact UX Pilot Menu Structure */}
      <header 
        className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0] px-4 lg:px-8 pb-3"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <div className="flex justify-between items-center mb-3 gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Kembali"
                title="Kembali"
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] cursor-pointer hover:border-[#0056D2]/40 transition-colors shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h1 className="text-xl font-bold text-[#0A192F] truncate">Leton Menu</h1>
          </div>
          <button
            type="button"
            onClick={onChangeOutlet}
            className="flex items-center gap-1.5 bg-[#F8FAFC] px-3 py-1.5 rounded-full border border-[#E2E8F0] cursor-pointer hover:border-[#0056D2]/40 transition-colors"
          >
            <Store className="w-3 h-3 text-[#0056D2]" />
            <span className="text-[11px] font-bold text-[#0A192F] truncate max-w-[140px]">
              {outlet.name}
            </span>
            <ChevronRight className="w-3 h-3 text-[#0A192F] rotate-90" />
          </button>
        </div>

        {/* Warning Banner if outlet is closed */}
        {!isOutletOpen && (
          <div className="mb-3 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-rose-800 text-xs flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>
                Outlet <strong>{outlet.name}</strong> sedang tutup.
              </span>
            </div>
            <button
              type="button"
              onClick={onChangeOutlet}
              className="text-xs font-bold text-rose-700 underline shrink-0 cursor-pointer"
            >
              Ganti Outlet
            </button>
          </div>
        )}

        {/* Search & Categories */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search menu..."
            className="w-full bg-[#F8FAFC] border-none rounded-xl py-2.5 pl-10 pr-4 text-base sm:text-sm text-[#0A192F] placeholder:text-slate-400 focus:ring-2 focus:ring-[#0056D2]/20 transition-all outline-none"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-xs whitespace-nowrap transition-all cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-[#0A192F] text-white font-bold'
                : 'bg-[#F8FAFC] text-slate-500 border border-[#E2E8F0] font-semibold'
            }`}
          >
            All Menu
          </button>

          {categoriesList.map((cat) => {
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs whitespace-nowrap transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#0A192F] text-white font-bold'
                    : 'bg-[#F8FAFC] text-slate-500 border border-[#E2E8F0] font-semibold'
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </header>

      <main className="px-4 lg:px-8 mt-6">
        <div className="mb-4 flex items-center gap-2">
          <h2 className="font-bold text-base text-[#0A192F]">
            {selectedCategory === 'all'
              ? 'Espresso Based & Signature'
              : categoriesList.find((c) => c.id === selectedCategory)?.name || 'Menu'}
          </h2>
          <span className="h-[1px] flex-1 bg-[#E2E8F0]" />
        </div>

        {filteredItems.length === 0 ? (
          <div className="py-16 text-center rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0]">
            <Coffee className="w-10 h-10 text-slate-300 mx-auto mb-2.5" />
            <p className="font-bold text-[#0A192F] text-sm">Menu Tidak Ditemukan</p>
            <p className="text-slate-500 text-xs mt-1">
              Coba kata kunci pencarian lain atau pilih kategori berbeda.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {filteredItems.map((item, idx) => {
              const qtyInCart = getProductQuantityInCart(item.id);
              const isAvailable = isMenuItemAvailableForOutlet(item, outlet?.id);
              const fallbackImg =
                UX_PILOT_MENU_FALLBACKS[idx % UX_PILOT_MENU_FALLBACKS.length];
              const resolvedImg = resolveMediaUrl(item.image) || fallbackImg;

              return (
                <div
                  key={item.id}
                  onClick={() => isAvailable && handleOpenAddOns(item)}
                  className={`bg-white border rounded-2xl overflow-hidden flex flex-col shadow-sm transition-all ${
                    qtyInCart > 0
                      ? 'border-[#0056D2] ring-1 ring-[#0056D2]/20'
                      : 'border-[#E2E8F0]'
                  } ${isAvailable ? 'cursor-pointer hover:shadow-md' : 'opacity-70 cursor-not-allowed'}`}
                >
                  <div className="h-44 lg:h-52 bg-[#F8FAFC] relative overflow-hidden">
                    <img
                      src={resolvedImg}
                      alt={item.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (target.src !== fallbackImg) {
                          target.src = fallbackImg;
                        }
                      }}
                    />

                    {item.badge && (
                      <div className="absolute top-2 left-2 bg-[#0056D2] text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase">
                        {item.badge}
                      </div>
                    )}

                    {!isAvailable && (
                      <div className="absolute inset-0 bg-[#0A192F]/60 backdrop-blur-[2px] flex items-center justify-center">
                        <span className="px-2.5 py-1 rounded-lg bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wider">
                          SOLD OUT
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-3 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-bold text-sm leading-tight mb-1 text-[#0A192F] line-clamp-1">
                        {item.name}
                      </h3>
                      <p className="text-[10px] text-slate-500 line-clamp-2">
                        {item.description || 'Smooth espresso with silky milk'}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <span className="font-bold text-[#0A192F] text-sm">
                        {formatRupiah(item.price)}
                      </span>

                      {!isAvailable ? (
                        <span className="text-[10px] font-semibold text-slate-400 bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-1 rounded-lg">
                          Habis
                        </span>
                      ) : qtyInCart > 0 ? (
                        <div className="flex items-center gap-1">
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-[#0056D2] text-[10px] font-bold">
                            <Check className="w-2.5 h-2.5" />
                            <span>{qtyInCart}</span>
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAddOns(item);
                            }}
                            className="w-8 h-8 bg-[#0056D2] text-white rounded-lg flex items-center justify-center cursor-pointer"
                            title="Tambah lagi"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAddOns(item);
                          }}
                          className="w-8 h-8 bg-[#0056D2] text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors cursor-pointer"
                          title="Tambah ke Pesanan"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Cart Floating Summary — Exact UX Pilot Floating Bar */}
      <AnimatePresence>
        {totalCartCount > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-20 lg:bottom-6 left-4 right-4 max-w-lg md:max-w-2xl mx-auto bg-[#0A192F] text-white p-4 rounded-2xl flex items-center justify-between shadow-xl z-40 animate-bounce-subtle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0056D2] rounded-xl flex items-center justify-center relative shrink-0">
                <ShoppingBag className="w-4 h-4 text-white" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-[#0A192F] text-[9px] font-bold rounded-full flex items-center justify-center">
                  {totalCartCount}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold text-white">
                  {totalCartCount} {totalCartCount === 1 ? 'Item' : 'Items'} added
                </p>
                <p className="text-[10px] text-slate-400">
                  Estimate: {formatRupiah(totalCartAmount)}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onOpenCart}
              id="view-cart-bottom-bar-btn"
              className="text-xs font-bold bg-[#0056D2] text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-600 transition-colors"
            >
              View Cart
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Add-Ons Customization Modal */}
      <ProductAddOnsModal
        product={customizingProduct}
        outletId={outlet?.id}
        isOpen={!!customizingProduct}
        onClose={() => setCustomizingProduct(null)}
        onConfirm={handleConfirmAddOns}
      />
    </div>
  );
};
